// Mock fixtures for local dev. Seeded from
// /Users/laurameans/Documents/projects/projects-dashboard/projects.json
// and shaped into the richer Supabase `projects` schema described in
// /Users/laurameans/.claude/plans/async-riding-cascade.md.
//
// The dashboard auto-falls-back to these when VITE_SUPABASE_URL is unset, so
// `npm run dev` is reviewable without any infrastructure.

import type {
  Agent,
  Approval,
  DispatchRow,
  EmbeddedDashboard,
  Heartbeat,
  Note,
  Project,
  ProjectNote,
  ProjectTask,
  Run,
  Service,
} from "../types/database";

const now = Date.now();
const minutes = (n: number) => new Date(now - n * 60_000).toISOString();
const hours = (n: number) => new Date(now - n * 3_600_000).toISOString();
const days = (n: number) => new Date(now - n * 86_400_000).toISOString();

export const mockProjects: Project[] = [
  {
    id: "meansalot",
    name: "Means a Lot",
    tagline: "Memorial app · iOS port phase 2",
    repo_path: "/Users/laurameans/Documents/projects/sagaholm",
    claude_md_path: "/Users/laurameans/Documents/projects/sagaholm/CLAUDE.md",
    last_run_at: hours(2),
    health: "yellow",
    health_reason: "5 days to memorial deadline; cartoon pipeline mid-rebuild",
    last_progress_at: hours(2),
    last_ship_at: days(3),
    streak_days: 4,
    revenue_potential_usd: null,
    audience_size: null,
    seasonality: null,
    strategic_fit: 5,
    blockers: ["Cartoon pipeline scaffolded but not wired to fal.ai workflow"],
    dependencies: ["fal.ai workflow built by hand"],
    uncertainty_level: 2,
    cost_risk: 2,
    next_action: "Wire NBP 4K char card → 9-grid scene gen → Grok video per cell",
    next_action_difficulty: "medium",
    energy_match: "medium",
    last_run_summary: "meanie-meansalot reviewed cartoon pipeline; 1 approval queued",
    last_recommendation: "2-hour focused session — low-energy refactor work fits your evening",
    open_approval_count: 1,
    agent_confidence: 0.78,
    momentum_label: "high",
    deadline: "2026-05-02",
    deadline_label: "5d deadline",
  },
  {
    id: "tagral",
    name: "Tagral",
    tagline: "iOS dot scanner · Phase 1.6 centering fix ready",
    repo_path: "/Users/laurameans/Documents/projects/tagral",
    claude_md_path: "/Users/laurameans/Documents/projects/tagral/CLAUDE.md",
    last_run_at: minutes(45),
    health: "green",
    health_reason: "3 commits ahead, ready to ship",
    last_progress_at: minutes(45),
    last_ship_at: days(8),
    streak_days: 11,
    revenue_potential_usd: 1500,
    audience_size: 800,
    seasonality: null,
    strategic_fit: 4,
    blockers: [],
    dependencies: [],
    uncertainty_level: 1,
    cost_risk: 1,
    next_action: "Push 3 commits to row1/main — includes Phase 1.6 centering fix",
    next_action_difficulty: "low",
    energy_match: "high",
    last_run_summary: "Centering fix verified; awaiting deploy approval",
    last_recommendation: "Ship it. 30-minute task, validated locally.",
    open_approval_count: 1,
    agent_confidence: 0.92,
    momentum_label: "high",
    deadline: null,
    deadline_label: null,
  },
  {
    id: "row1",
    name: "Row1",
    tagline: "Flight tracker · build 75 last shipped Mar 30",
    repo_path: "/Users/laurameans/Documents/projects/row1",
    claude_md_path: "/Users/laurameans/Documents/projects/row1/CLAUDE.md",
    last_run_at: days(6),
    health: "yellow",
    health_reason: "Stalled 28 days; XP/gamification reverted",
    last_progress_at: days(28),
    last_ship_at: days(28),
    streak_days: 0,
    revenue_potential_usd: 200,
    audience_size: 150,
    seasonality: "year-round",
    strategic_fit: 3,
    blockers: ["AeroAPI cost model unclear", "XP rework needed"],
    dependencies: ["FlightAware AeroAPI"],
    uncertainty_level: 3,
    cost_risk: 3,
    next_action: "Finish process-status-change → push pipeline; revisit AeroAPI cost model",
    next_action_difficulty: "high",
    energy_match: "high",
    last_run_summary: "No agent runs in 6 days",
    last_recommendation: "Schedule a 4-hour deep-work session this week",
    open_approval_count: 0,
    agent_confidence: 0.55,
    momentum_label: "stalled 28d",
    deadline: null,
    deadline_label: null,
  },
  {
    id: "glp1",
    name: "GLP-1 Education",
    tagline: "Affiliate site · 5 dossiers ready · 3 pages drafting",
    repo_path: "/Users/laurameans/Documents/projects/glp1-education",
    claude_md_path: null,
    last_run_at: hours(1),
    health: "green",
    health_reason: "Accelerating — 3 pages mid-draft",
    last_progress_at: hours(1),
    last_ship_at: null,
    streak_days: 5,
    revenue_potential_usd: 4000,
    audience_size: 12000,
    seasonality: "year-round",
    strategic_fit: 4,
    blockers: ["No affiliate deals signed yet"],
    dependencies: ["Telehealth partner outreach"],
    uncertainty_level: 2,
    cost_risk: 1,
    next_action: "Finalize evidence-page outlines and start drafting",
    next_action_difficulty: "medium",
    energy_match: "medium",
    last_run_summary: "meanie-glp1 drafted 3 evidence-page outlines",
    last_recommendation: "Review outlines tomorrow morning — fresh eyes",
    open_approval_count: 0,
    agent_confidence: 0.82,
    momentum_label: "accelerating",
  },
  {
    id: "knowyourcookware",
    name: "Know Your Cookware",
    tagline: "Live · 17 pages · GSC pending · affiliate links staged",
    repo_path: "/Users/laurameans/Documents/projects/knowyourcookware",
    claude_md_path: null,
    last_run_at: hours(8),
    health: "green",
    health_reason: "Live and ready",
    last_progress_at: days(1),
    last_ship_at: days(1),
    streak_days: 2,
    revenue_potential_usd: 800,
    audience_size: 5000,
    seasonality: "Q4 spike",
    strategic_fit: 3,
    blockers: [],
    dependencies: [],
    uncertainty_level: 1,
    cost_risk: 1,
    next_action: "Verify in Google Search Console; flip on Amazon Associates",
    next_action_difficulty: "low",
    energy_match: "low",
    last_run_summary: "Site healthy; affiliate links staged but inactive",
    last_recommendation: "10 minutes to flip GSC; easy win",
    open_approval_count: 0,
    agent_confidence: 0.95,
    momentum_label: "ready",
  },
  {
    id: "barkpace",
    name: "BarkPace",
    tagline: "Apple Watch dog fitness — Hackathon Apr 21–26",
    repo_path: "/Users/laurameans/Documents/projects/barkpace",
    claude_md_path: null,
    last_run_at: days(2),
    health: "red",
    health_reason: "Hackathon ends in days; no codebase yet",
    last_progress_at: days(2),
    last_ship_at: null,
    streak_days: 0,
    revenue_potential_usd: 0,
    audience_size: 0,
    seasonality: null,
    strategic_fit: 4,
    blockers: ["No codebase yet", "barkpace.com not purchased"],
    dependencies: ["Hackathon team confirmation"],
    uncertainty_level: 4,
    cost_risk: 2,
    next_action: "Initialize Expo + WatchOS companion; the demo is the product",
    next_action_difficulty: "high",
    energy_match: "high",
    last_run_summary: "Plan dump complete; 6 research dossiers indexed",
    last_recommendation: "Today: scaffold the iOS app and wire one MCP tool",
    open_approval_count: 0,
    agent_confidence: 0.4,
    momentum_label: "race",
  },
];

export const mockAgents: Agent[] = [
  { id: "a1", name: "meanie-overseer", tier: "overseer", project: null, heartbeat_at: minutes(18) },
  { id: "a2", name: "meanie-meansalot", tier: "lead", project: "meansalot", heartbeat_at: hours(2) },
  { id: "a3", name: "meanie-tagral", tier: "lead", project: "tagral", heartbeat_at: minutes(45) },
  { id: "a4", name: "meanie-row1", tier: "lead", project: "row1", heartbeat_at: days(6) },
  { id: "a5", name: "meanie-glp1", tier: "lead", project: "glp1", heartbeat_at: hours(1) },
  { id: "a6", name: "meanie-deployer", tier: "specialist", project: null, heartbeat_at: hours(4) },
  { id: "a7", name: "meanie-researcher", tier: "specialist", project: null, heartbeat_at: hours(11) },
];

export const mockRuns: Run[] = [
  {
    id: "r1",
    agent: "meanie-tagral",
    project: "tagral",
    started_at: minutes(60),
    ended_at: minutes(45),
    status: "completed",
    summary: "Verified Phase 1.6 centering fix; opened approval to push 3 commits.",
    cost_tokens: 18400,
    resume_state: null,
  },
  {
    id: "r2",
    agent: "meanie-meansalot",
    project: "meansalot",
    started_at: hours(2.5),
    ended_at: hours(2),
    status: "completed",
    summary: "Reviewed cartoon pipeline; flagged fal.ai workflow handoff for human.",
    cost_tokens: 22100,
    resume_state: null,
  },
  {
    id: "r3",
    agent: "meanie-glp1",
    project: "glp1",
    started_at: hours(1.2),
    ended_at: hours(1),
    status: "completed",
    summary: "Drafted three evidence-page outlines (compounding risk, telehealth options, real-world side-effects).",
    cost_tokens: 31200,
    resume_state: null,
  },
  {
    id: "r4",
    agent: "meanie-overseer",
    project: "row1",
    started_at: minutes(20),
    ended_at: minutes(18),
    status: "completed",
    summary: "Stale: no project run in 28 days. Recommending a focused session this week.",
    cost_tokens: 4200,
    resume_state: null,
  },
];

export const mockApprovals: Approval[] = [
  {
    id: "ap1",
    project: "tagral",
    agent: "meanie-tagral",
    action: "deploy:eas-build",
    payload: { branch: "main", commits: 3, version: "0.4.2" },
    class: "blocking",
    status: "pending",
    requested_at: minutes(45),
    decided_at: null,
    expires_at: hours(-24),
  },
  {
    id: "ap2",
    project: "meansalot",
    agent: "meanie-meansalot",
    action: "fal:reset-workflow",
    payload: { workflow_id: "cartoon-v3" },
    class: "blocking",
    status: "pending",
    requested_at: hours(2),
    decided_at: null,
    expires_at: null,
  },
  {
    id: "ap3",
    project: "glp1",
    agent: "meanie-glp1",
    action: "review:evidence-outlines",
    payload: { count: 3 },
    class: "notify",
    status: "pending",
    requested_at: hours(1),
    decided_at: null,
    expires_at: null,
  },
  {
    id: "ap4",
    project: "tagral",
    agent: "meanie-tagral",
    action: "scrape:lululemon",
    payload: { sku_count: 47 },
    class: "silent",
    status: "approved",
    requested_at: hours(5),
    decided_at: hours(5),
    expires_at: null,
  },
];

export const mockNotes: Note[] = [
  {
    id: "n1",
    project: "tagral",
    kind: "decision",
    body: "Phase 1.6 centering fix uses circular-average gradient for radius detection; do not narrow ring bounds (0.15–1.23 is correct).",
    superseded_by: null,
    created_at: hours(3),
  },
  {
    id: "n2",
    project: "meansalot",
    kind: "blocker",
    body: "fal.ai workflow is built by hand in the UI — not automatable, not shareable. Surrounding Next.js code is fair game.",
    superseded_by: null,
    created_at: days(2),
  },
  {
    id: "n3",
    project: "row1",
    kind: "decision",
    body: "XP/gamification reverted via migration 093 — caused issues with flight tracking. Re-approach later.",
    superseded_by: null,
    created_at: days(28),
  },
];

export const mockHeartbeats: Heartbeat[] = [
  { id: "h1", source: "overseer", payload: { ran: 1, queued: 2 }, created_at: minutes(18) },
  { id: "h2", source: "dispatcher", payload: { polled: 612 }, created_at: minutes(1) },
  { id: "h3", source: "overseer", payload: { ran: 1, queued: 1 }, created_at: minutes(48) },
  { id: "h4", source: "dispatcher", payload: { polled: 611 }, created_at: minutes(2) },
  { id: "h5", source: "overseer", payload: { ran: 1, queued: 0 }, created_at: hours(1.3) },
];

// V1.5 cockpit fixtures.

export const mockTasks: ProjectTask[] = [
  {
    id: "t1",
    project: "Tagral",
    title: "Push 3 commits to main (Phase 1.6 centering fix)",
    description: "Awaiting deploy approval. Local validation green.",
    status: "todo",
    kind: "deploy",
    priority: 5,
    agent_owner: "meanie-tagral",
    source: "specialist",
    parent_task_id: null,
    created_at: minutes(50),
    completed_at: null,
  },
  {
    id: "t2",
    project: "Tagral",
    title: "Switch Lululemon scraper to Wayback fallback",
    description: "Akamai WAF blocking direct fetch since Apr 25. Fallback playbook in scraper-akamai-sfcc-playbook.md.",
    status: "in_progress",
    kind: "bug",
    priority: 4,
    agent_owner: "meanie-tagral",
    source: "agent_note_extract",
    parent_task_id: null,
    created_at: hours(3),
    completed_at: null,
  },
  {
    id: "t3",
    project: "Tagral",
    title: "Depop seller playbook — finalize sample listing",
    description: null,
    status: "todo",
    kind: "content",
    priority: 2,
    agent_owner: null,
    source: "laura",
    parent_task_id: null,
    created_at: hours(20),
    completed_at: null,
  },
  {
    id: "t4",
    project: "Tagral",
    title: "Vuori scraper failing — radius detection regression",
    description: "Blocked on test image set; need 4 more captures.",
    status: "blocked",
    kind: "bug",
    priority: 3,
    agent_owner: "meanie-tagral",
    source: "specialist",
    parent_task_id: null,
    created_at: days(2),
    completed_at: null,
  },
  {
    id: "t5",
    project: "Tagral",
    title: "Phase 1.5 dot-scanner enhancements",
    description: null,
    status: "done",
    kind: "feature",
    priority: 4,
    agent_owner: "meanie-tagral",
    source: "specialist",
    parent_task_id: null,
    created_at: days(7),
    completed_at: days(1),
  },
  {
    id: "t6",
    project: "Means a Lot",
    title: "Wire NBP 4K char card → 9-grid scene gen → Grok video",
    description: "fal.ai workflow built by hand; replace scaffolded code.",
    status: "in_progress",
    kind: "feature",
    priority: 5,
    agent_owner: "meanie-meansalot",
    source: "specialist",
    parent_task_id: null,
    created_at: hours(2),
    completed_at: null,
  },
  {
    id: "t7",
    project: "Row1",
    title: "Re-approach XP/gamification (avoid flight tracking regression)",
    description: null,
    status: "blocked",
    kind: "feature",
    priority: 2,
    agent_owner: null,
    source: "laura",
    parent_task_id: null,
    created_at: days(28),
    completed_at: null,
  },
];

export const mockProjectNotes: ProjectNote[] = [
  {
    id: "pn1",
    project: "Tagral",
    body: "Lululemon scraper down — switch to Wayback fallback per scraper-akamai-sfcc-playbook.md. Re-test against 5 SKUs from the men's shorts category.",
    author: "laura",
    interpreted_by_agent_at: hours(2.5),
    agent_actions: [
      { kind: "create_task", title: "Switch Lululemon scraper to Wayback fallback", priority: 4 },
    ],
    created_at: hours(3),
  },
  {
    id: "pn2",
    project: "Tagral",
    body: "Depop listings — pricing rule: cross-list 15% above Poshmark equivalent to absorb Depop's 10% fee + buyer-pays-shipping bias.",
    author: "laura",
    interpreted_by_agent_at: null,
    agent_actions: null,
    created_at: minutes(8),
  },
  {
    id: "pn3",
    project: "Means a Lot",
    body: "fal.ai workflow built by hand in UI — not automatable, not shareable. Surrounding Next.js code is fair game.",
    author: "laura",
    interpreted_by_agent_at: days(2),
    agent_actions: [
      { kind: "create_note", title: "Decision logged in V1 notes table" },
    ],
    created_at: days(2),
  },
];

export const mockServices: Service[] = [
  {
    id: "s1",
    name: "Cloudflare",
    category: "hosting",
    account_email: "laurammeans@gmail.com",
    login_method: "email_password",
    plan: "pro",
    monthly_cost_usd: 25,
    renews_at: new Date(now + 14 * 86_400_000).toISOString().slice(0, 10),
    dashboard_url: "https://dash.cloudflare.com",
    used_by: ["Tagral", "Row1", "Means a Lot", "Meanie", "Know Your Cookware", "GLP-1 Education"],
    status: "healthy",
    last_checked_at: minutes(35),
    notes: "Pages + Workers + DNS. Foundation of the portfolio.",
    created_at: days(120),
    updated_at: minutes(35),
  },
  {
    id: "s2",
    name: "Supabase",
    category: "db",
    account_email: "laura@meansalot.com",
    login_method: "github_oauth",
    plan: "pro",
    monthly_cost_usd: 25,
    renews_at: new Date(now + 21 * 86_400_000).toISOString().slice(0, 10),
    dashboard_url: "https://supabase.com/dashboard",
    used_by: ["Meanie", "Means a Lot", "Row1", "Roots & Extensions"],
    status: "healthy",
    last_checked_at: minutes(20),
    notes: "Two projects: meanie + sagaholm.",
    created_at: days(180),
    updated_at: minutes(20),
  },
  {
    id: "s3",
    name: "fal.ai",
    category: "ai",
    account_email: "laura@meansalot.com",
    login_method: "google_oauth",
    plan: "paid",
    monthly_cost_usd: 40,
    renews_at: null,
    dashboard_url: "https://fal.ai/dashboard",
    used_by: ["Means a Lot", "Row1"],
    status: "healthy",
    last_checked_at: hours(2),
    notes: "Nano Banana 2 + Grok Imagine. Pay-as-you-go.",
    created_at: days(90),
    updated_at: hours(2),
  },
  {
    id: "s4",
    name: "Apple Developer",
    category: "apple",
    account_email: "laura@meansalot.com",
    login_method: "apple_id",
    plan: "paid",
    monthly_cost_usd: 8.25,
    renews_at: new Date(now + 5 * 86_400_000).toISOString().slice(0, 10),
    dashboard_url: "https://developer.apple.com/account",
    used_by: ["Tagral", "Row1", "Meanie", "BarkPace"],
    status: "warning",
    last_checked_at: hours(6),
    notes: "Team HZ46FJ3RF8. Annual renewal soon.",
    created_at: days(360),
    updated_at: hours(6),
  },
  {
    id: "s5",
    name: "OpenAI",
    category: "ai",
    account_email: "laurammeans@gmail.com",
    login_method: "google_oauth",
    plan: "paid",
    monthly_cost_usd: 18,
    renews_at: null,
    dashboard_url: "https://platform.openai.com",
    used_by: ["GLP-1 Education", "Know Your Cookware"],
    status: "healthy",
    last_checked_at: hours(1),
    notes: "Used sparingly; Anthropic Max plan covers most.",
    created_at: days(200),
    updated_at: hours(1),
  },
  {
    id: "s6",
    name: "Anthropic",
    category: "ai",
    account_email: "laura@meansalot.com",
    login_method: "google_oauth",
    plan: "paid",
    monthly_cost_usd: 200,
    renews_at: new Date(now + 11 * 86_400_000).toISOString().slice(0, 10),
    dashboard_url: "https://console.anthropic.com",
    used_by: ["Meanie", "Tagral", "Row1", "Means a Lot", "Roots & Extensions", "BarkPace", "GLP-1 Education", "Know Your Cookware"],
    status: "healthy",
    last_checked_at: minutes(4),
    notes: "Max plan. Covers all Claude Code work.",
    created_at: days(150),
    updated_at: minutes(4),
  },
  {
    id: "s7",
    name: "GitHub",
    category: "hosting",
    account_email: "laurammeans@gmail.com",
    login_method: "email_password",
    plan: "free",
    monthly_cost_usd: 0,
    renews_at: null,
    dashboard_url: "https://github.com/laurameans",
    used_by: ["Row1", "Tagral", "Meanie"],
    status: "healthy",
    last_checked_at: hours(12),
    notes: null,
    created_at: days(900),
    updated_at: hours(12),
  },
];

export const mockEmbeds: EmbeddedDashboard[] = [
  {
    id: "ed1",
    project: "Tagral",
    label: "Tagral Dashboard",
    url: "https://dashboard.tagral.com",
    category: "admin",
    iframe_safe: true,
    height_px: 600,
    display_order: 0,
    created_at: days(20),
  },
  {
    id: "ed2",
    project: "Row1",
    label: "Row1 Flight Deck",
    url: "https://row1-flightdeck.pages.dev",
    category: "monitoring",
    iframe_safe: true,
    height_px: 600,
    display_order: 0,
    created_at: days(35),
  },
  {
    id: "ed3",
    project: "Means a Lot",
    label: "Means a Lot — Live",
    url: "https://remember.meansalot.com",
    category: "admin",
    iframe_safe: true,
    height_px: 600,
    display_order: 0,
    created_at: days(15),
  },
];

export const mockDispatch: DispatchRow[] = [
  {
    id: "d1",
    kind: "summarize",
    project: "tagral",
    payload: null,
    status: "done",
    enqueued_at: hours(1),
    started_at: hours(1),
    finished_at: minutes(45),
  },
  {
    id: "d2",
    kind: "resume_run",
    project: "meansalot",
    payload: { run_id: "r2" },
    status: "pending",
    enqueued_at: minutes(8),
    started_at: null,
    finished_at: null,
  },
];
